import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  AcademicCapIcon,
  GlobeAltIcon,
  HeartIcon,
  LightBulbIcon,
  UserGroupIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import Navigation from '../../common/Navigation';
import Footer from '../../common/Footer';

const SocialImpact: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'scholarship' | 'esg-policy' | 'impact'>('overview');

  const impactMetrics = [
    {
      label: 'Companies Funded',
      value: '150+',
      description: 'Growing businesses supported'
    },
    {
      label: 'Capital Deployed',
      value: '$500M+',
      description: 'In equity financing'
    },
    {
      label: 'Jobs Created',
      value: '10,000+',
      description: 'Through portfolio companies'
    },
    {
      label: 'Students Supported',
      value: '25',
      description: 'Full scholarships awarded'
    }
  ];

  const esgPillars = [
    {
      icon: GlobeAltIcon,
      title: 'Environmental',
      points: [
        'Carbon-neutral operations by 2025',
        'Paperless transaction processing',
        'Support for clean tech companies',
        'Remote-first work culture'
      ]
    },
    {
      icon: UserGroupIcon,
      title: 'Social',
      points: [
        'Annual scholarship program',
        'Diversity & inclusion initiatives',
        'Financial literacy programs',
        'Community partnerships'
      ]
    },
    {
      icon: ChartBarIcon,
      title: 'Governance',
      points: [
        'Transparent reporting practices',
        'Ethical investment guidelines',
        'Board diversity commitment',
        'Regulatory compliance'
      ]
    }
  ];

  const scholarshipRequirements = [
    'Enrolled in an accredited university',
    'Minimum 3.0 GPA',
    'Demonstrated financial need',
    'Commitment to community service',
    'Interest in finance or entrepreneurship'
  ];

  const scholarshipBenefits = [
    'Full tuition coverage',
    'Annual book stipend',
    'Mentorship program',
    'Summer internship opportunities',
    'Professional development workshops',
    'Networking events'
  ];

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-financial">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1 
            className="text-5xl font-bold text-primary-950 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Creating Positive Impact
          </motion.h1>
          <motion.p 
            className="text-xl text-primary-700 max-w-3xl mx-auto mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            At Twenty Four Capital, we believe in doing well by doing good. Our commitment to 
            environmental, social, and governance principles drives everything we do.
          </motion.p>

          {/* Tab Navigation */}
          <motion.div 
            className="flex flex-wrap justify-center gap-4 mt-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {[
              { id: 'overview', label: 'Overview', icon: SparklesIcon },
              { id: 'scholarship', label: 'Scholarship Program', icon: AcademicCapIcon },
              { id: 'esg-policy', label: 'ESG Policy', icon: DocumentTextIcon },
              { id: 'impact', label: 'Impact Metrics', icon: ChartBarIcon }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-primary-600 text-white shadow-lg'
                    : 'bg-white text-primary-700 hover:bg-primary-50'
                }`}
              >
                <tab.icon className="h-5 w-5" />
                {tab.label}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            {/* Impact Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
              {impactMetrics.map((metric, index) => (
                <motion.div
                  key={metric.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-md p-6 text-center"
                >
                  <p className="text-4xl font-bold text-primary-600 mb-2">{metric.value}</p>
                  <p className="text-lg font-semibold text-primary-950 mb-1">{metric.label}</p>
                  <p className="text-sm text-gray-600">{metric.description}</p>
                </motion.div>
              ))}
            </div>

            {/* ESG Pillars */}
            <h2 className="text-3xl font-bold text-primary-950 text-center mb-12">
              Our ESG Commitment
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {esgPillars.map((pillar, index) => (
                <motion.div
                  key={pillar.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-md p-8"
                >
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-6">
                    <pillar.icon className="h-8 w-8 text-primary-600" />
                  </div>
                  <h3 className="text-2xl font-semibold text-primary-950 mb-4">
                    {pillar.title}
                  </h3>
                  <ul className="space-y-3">
                    {pillar.points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{point}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Scholarship Program Tab */}
      {activeTab === 'scholarship' && (
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white p-8">
                <div className="flex items-center gap-4 mb-4">
                  <AcademicCapIcon className="h-12 w-12" />
                  <h2 className="text-3xl font-bold">
                    Twenty Four Capital Scholarship Program
                  </h2>
                </div>
                <p className="text-lg text-primary-100">
                  Investing in the next generation of financial leaders
                </p>
              </div>

              <div className="p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                  <div>
                    <h3 className="text-2xl font-semibold text-primary-950 mb-6">
                      Program Overview
                    </h3>
                    <p className="text-gray-700 mb-6">
                      The Twenty Four Capital Scholarship Program provides full tuition coverage 
                      for exceptional students pursuing degrees in finance, business, or related 
                      fields. We believe in removing financial barriers to education and creating 
                      opportunities for talented individuals from all backgrounds.
                    </p>
                    <p className="text-gray-700 mb-6">
                      Recipients receive continued support throughout their academic journey, 
                      contingent upon maintaining a minimum 3.0 GPA and 90% attendance rate.
                    </p>

                    <h4 className="text-xl font-semibold text-primary-950 mb-4">
                      Eligibility Requirements
                    </h4>
                    <ul className="space-y-3 mb-8">
                      {scholarshipRequirements.map((req, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-primary-950 mb-4">
                      Scholarship Benefits
                    </h4>
                    <ul className="space-y-3 mb-8">
                      {scholarshipBenefits.map((benefit, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <HeartIcon className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{benefit}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="bg-primary-50 rounded-lg p-6">
                      <h4 className="text-lg font-semibold text-primary-950 mb-3">
                        Application Timeline
                      </h4>
                      <ul className="space-y-2 text-sm">
                        <li className="flex justify-between">
                          <span className="text-gray-700">Applications Open</span>
                          <span className="font-medium">January 1</span>
                        </li>
                        <li className="flex justify-between">
                          <span className="text-gray-700">Application Deadline</span>
                          <span className="font-medium">March 31</span>
                        </li>
                        <li className="flex justify-between">
                          <span className="text-gray-700">Interviews</span>
                          <span className="font-medium">April - May</span>
                        </li>
                        <li className="flex justify-between">
                          <span className="text-gray-700">Recipients Announced</span>
                          <span className="font-medium">June 1</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="mt-12 text-center">
                  <Link
                    to="/scholarship-application"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Apply for Scholarship
                    <AcademicCapIcon className="h-5 w-5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ESG Policy Tab */}
      {activeTab === 'esg-policy' && (
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl font-bold text-primary-950">
                  ESG Policy Document
                </h2>
                <button className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                  <ArrowDownTrayIcon className="h-5 w-5" />
                  Download PDF
                </button>
              </div>

              <div className="prose prose-lg max-w-none">
                <h3 className="text-xl font-semibold text-primary-950 mb-4">
                  1. Introduction
                </h3>
                <p className="text-gray-700 mb-6">
                  Twenty Four Capital is committed to integrating Environmental, Social, and 
                  Governance (ESG) considerations into our business operations and investment 
                  decisions. This policy outlines our approach to responsible business practices 
                  and sustainable value creation.
                </p>

                <h3 className="text-xl font-semibold text-primary-950 mb-4">
                  2. Environmental Commitment
                </h3>
                <p className="text-gray-700 mb-4">
                  We recognize our responsibility to minimize environmental impact:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                  <li>Achieve carbon neutrality in operations by 2025</li>
                  <li>Prioritize investments in sustainable technologies</li>
                  <li>Implement paperless processes wherever possible</li>
                  <li>Encourage remote work to reduce commuting emissions</li>
                </ul>

                <h3 className="text-xl font-semibold text-primary-950 mb-4">
                  3. Social Responsibility
                </h3>
                <p className="text-gray-700 mb-4">
                  Our social initiatives focus on creating positive community impact:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                  <li>Annual scholarship program for underserved students</li>
                  <li>Financial literacy workshops in local communities</li>
                  <li>Diversity and inclusion in hiring and partnerships</li>
                  <li>Support for minority and women-owned businesses</li>
                </ul>

                <h3 className="text-xl font-semibold text-primary-950 mb-4">
                  4. Governance Standards
                </h3>
                <p className="text-gray-700 mb-4">
                  We maintain the highest standards of corporate governance:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                  <li>Transparent reporting and disclosure practices</li>
                  <li>Independent board oversight and diverse representation</li>
                  <li>Robust risk management and compliance frameworks</li>
                  <li>Ethical business conduct and anti-corruption policies</li>
                </ul>

                <h3 className="text-xl font-semibold text-primary-950 mb-4">
                  5. Implementation and Monitoring
                </h3>
                <p className="text-gray-700 mb-6">
                  This policy is reviewed annually and progress is reported quarterly to 
                  stakeholders. All employees receive ESG training and are expected to 
                  uphold these principles in their daily work.
                </p>

                <div className="bg-primary-50 rounded-lg p-6 mt-8">
                  <p className="text-sm text-gray-600">
                    <strong>Last Updated:</strong> January 1, 2024<br />
                    <strong>Next Review:</strong> January 1, 2025<br />
                    <strong>Policy Owner:</strong> ESG Committee
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Impact Metrics Tab */}
      {activeTab === 'impact' && (
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-primary-950 text-center mb-12">
              Measuring Our Impact
            </h2>

            {/* Annual Impact Report */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-semibold text-primary-950 mb-4">
                    2023 Impact Report
                  </h3>
                  <p className="text-gray-700 mb-6">
                    Our annual impact report provides a comprehensive overview of our ESG 
                    initiatives, progress towards goals, and the positive changes we've 
                    helped create in communities worldwide.
                  </p>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center gap-3">
                      <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      <span className="text-gray-700">25% reduction in carbon footprint</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      <span className="text-gray-700">$2M in scholarships awarded</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      <span className="text-gray-700">40% women in leadership roles</span>
                    </li>
                  </ul>
                  <button className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                    <ArrowDownTrayIcon className="h-5 w-5" />
                    Download Full Report
                  </button>
                </div>
                <div className="relative">
                  <img
                    src="/images/impact-report-cover.jpg"
                    alt="2023 Impact Report"
                    className="rounded-lg shadow-xl"
                  />
                  <div className="absolute top-4 right-4 bg-white rounded-full p-3 shadow-lg">
                    <DocumentTextIcon className="h-8 w-8 text-primary-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Impact Calculator */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl text-white p-8">
              <h3 className="text-2xl font-bold mb-4">
                Calculate Your Impact
              </h3>
              <p className="text-primary-100 mb-6">
                See how your partnership with Twenty Four Capital contributes to positive change.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                  <label className="block text-sm font-medium mb-2">Facility Amount ($)</label>
                  <input
                    type="number"
                    placeholder="1000000"
                    className="w-full px-4 py-2 bg-white/20 rounded-lg placeholder-white/50 text-white focus:outline-none focus:ring-2 focus:ring-white/50"
                  />
                </div>
                <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                  <label className="block text-sm font-medium mb-2">Industry</label>
                  <select className="w-full px-4 py-2 bg-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-white/50">
                    <option>Technology</option>
                    <option>Healthcare</option>
                    <option>Clean Energy</option>
                    <option>Education</option>
                  </select>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                  <label className="block text-sm font-medium mb-2">Estimated Jobs</label>
                  <div className="text-2xl font-bold">~50</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
    <Footer />
    </>
  );
};

export default SocialImpact; 